import { Link, createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import {
  getGetAdminSettingsQueryKey,
  useGetAdminSettings,
  useUpdateAdminSettings,
} from '../../../api/generated/endpoints/admin/admin'
import { getGetAvailabilityQueryKey } from '../../../api/generated/endpoints/availability/availability'
import type { SettingsRead } from '../../../api/generated/model'
import { BaysSection } from '../../../components/BaysSection'
import { CoachAbsences } from '../../../components/CoachAbsences'
import { apiProblems } from '../../../lib/api-error'
import { formatUYU } from '../../../lib/money'

/**
 * The business rules, without a database console.
 *
 * Every one of these lived in `settings` and `products` from M1 and none was
 * reachable except by SQL, which made "open an hour earlier on Saturdays" a
 * developer task.
 *
 * ## Why each card saves on its own
 *
 * One form with one Save at the bottom means a coach who came to change the
 * price scrolls past the opening hours, the horizon and the advanced section
 * to find the button, and any accidental keystroke on the way goes with it.
 * A card that saves itself keeps cause and effect within one glance — which
 * matters more here than the extra requests cost.
 */
export const Route = createFileRoute('/_authed/_admin/admin/settings')({
  component: SettingsPage,
})

function SettingsPage() {
  const settings = useGetAdminSettings()

  if (settings.isPending) return <p className="text-slate-600">Cargando…</p>
  if (settings.isError) {
    return (
      <p role="alert" className="text-red-700">
        No se pudieron cargar los ajustes.
      </p>
    )
  }

  const current = settings.data

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-semibold">Ajustes</h1>
        <p className="mt-2 max-w-2xl text-slate-700">
          Los cambios se aplican al instante; no hay que reiniciar nada.
        </p>
      </div>

      {current.bookings_outside_hours > 0 && (
        <p
          role="status"
          className="rounded-lg border-2 border-amber-400 bg-amber-50 p-5 text-amber-900"
        >
          <strong>
            {current.bookings_outside_hours}{' '}
            {current.bookings_outside_hours === 1 ? 'reserva queda' : 'reservas quedan'} fuera de
            este horario.
          </strong>{' '}
          Siguen en pie y los simuladores siguen reservados: cambiar el horario no cancela nada. Si
          no era la idea, se puede volver a ampliar el horario o cancelarlas desde el calendario.
        </p>
      )}

      <BaysSection />
      <Hours current={current} />
      <Prices current={current} />
      <Rules current={current} />
      <Coaches current={current} />
      <CoachAbsences opensAt={current.opening_time} closesAt={current.closing_time} />
      <Reminders current={current} />
      <Advanced current={current} />
    </div>
  )
}

/** Everything a card needs to save itself. */
function useSave() {
  const queryClient = useQueryClient()
  return useUpdateAdminSettings({
    mutation: {
      retry: false,
      onSuccess: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: getGetAdminSettingsQueryKey() }),
          // Hours, prices and the horizon all show on the public calendar.
          queryClient.invalidateQueries({ queryKey: getGetAvailabilityQueryKey() }),
        ]),
    },
  })
}

function Card({
  title,
  hint,
  dirty,
  save,
  children,
}: {
  title: string
  hint?: string
  dirty: boolean
  save: ReturnType<typeof useSave>
  children: React.ReactNode
}) {
  const [justSaved, setJustSaved] = useState(false)
  useEffect(() => {
    if (!save.isSuccess) return
    setJustSaved(true)
    const timer = setTimeout(() => setJustSaved(false), 3000)
    return () => clearTimeout(timer)
  }, [save.isSuccess, save.submittedAt])

  // The API sends every reason at once. Showing one at a time is how somebody
  // ends up saving four times.
  const problems = apiProblems(save.error, 'El cambio no se aplicó.')

  return (
    <section className="rounded-lg border-2 border-slate-300 bg-white p-6">
      <h2 className="text-2xl font-semibold">{title}</h2>
      {hint && <p className="mt-1 text-base text-slate-600">{hint}</p>}

      <div className="mt-5 space-y-5">{children}</div>

      {problems.length > 0 && (
        <ul role="alert" className="mt-5 space-y-1 rounded-md bg-red-50 p-4 text-red-800">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}

      <div className="mt-6 flex items-center gap-4">
        <button
          type="submit"
          disabled={!dirty || save.isPending}
          className="min-h-16 rounded-md bg-slate-900 px-8 text-xl text-white disabled:opacity-40 focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          {save.isPending ? 'Guardando…' : 'Guardar'}
        </button>
        {justSaved && !dirty && <span className="text-lg text-green-800">Guardado.</span>}
        {dirty && !save.isPending && <span className="text-lg text-amber-800">Sin guardar.</span>}
      </div>
    </section>
  )
}

const INPUT =
  'min-h-14 w-full max-w-xs rounded-md border-2 border-slate-400 px-4 text-xl focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900'

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="text-lg font-medium">{label}</span>
      {hint && <span className="block text-base text-slate-600">{hint}</span>}
      <span className="mt-1 block">{children}</span>
    </label>
  )
}

/** A card wrapped in a form, so Enter saves and the browser handles the rest. */
function CardForm({ onSubmit, ...props }: Parameters<typeof Card>[0] & { onSubmit: () => void }) {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
    >
      <Card {...props} />
    </form>
  )
}

// ---------------------------------------------------------------------------

function Hours({ current }: { current: SettingsRead }) {
  const save = useSave()
  const [opening, setOpening] = useState(current.opening_time)
  const [closing, setClosing] = useState(current.closing_time)
  const dirty = opening !== current.opening_time || closing !== current.closing_time

  return (
    <CardForm
      title="Horario de apertura"
      hint="Es el mismo todos los días, y la última sesión tiene que terminar a la hora de cierre. Para cerrar una fecha puntual está la pantalla de días cerrados."
      dirty={dirty}
      save={save}
      onSubmit={() => save.mutate({ data: { opening_time: opening, closing_time: closing } })}
    >
      <div className="flex flex-wrap gap-6">
        <Field label="Abre">
          <input
            type="time"
            value={opening}
            onChange={(event) => setOpening(event.target.value)}
            className={INPUT}
          />
        </Field>
        {/* No hint of its own: one under "Cierra" alone pushed its box below
            "Abre"'s, and two times that do not line up read as a mistake. */}
        <Field label="Cierra">
          <input
            type="time"
            value={closing}
            onChange={(event) => setClosing(event.target.value)}
            className={INPUT}
          />
        </Field>
      </div>
    </CardForm>
  )
}

function Prices({ current }: { current: SettingsRead }) {
  const save = useSave()
  const hour = current.hour_price_uyu_pesos
  const [pack, setPack] = useState(String(current.package_price_uyu_pesos))
  const [entry, setEntry] = useState(String(current.tournament_entry_price_uyu_pesos))

  const dirty =
    Number(pack) !== current.package_price_uyu_pesos ||
    Number(entry) !== current.tournament_entry_price_uyu_pesos

  const perHour = current.package_hours > 0 ? Math.round(Number(pack) / current.package_hours) : 0

  return (
    <CardForm
      title="Precios"
      hint="En pesos. Cambiar un precio nunca modifica lo que ya se le cobró a alguien."
      dirty={dirty}
      save={save}
      onSubmit={() =>
        save.mutate({
          data: {
            package_price_uyu_pesos: Number(pack),
            tournament_entry_price_uyu_pesos: Number(entry),
          },
        })
      }
    >
      {/* Bay prices live on their own screen since decision 0005: a price
          list, overridden per bay, does not fit in one number field. */}
      <div>
        <p className="text-lg">
          Las horas de simulador, las clases y las rondas tienen su propia pantalla.
        </p>
        <Link
          to="/admin/rates"
          className="mt-2 inline-flex min-h-14 items-center rounded-md border-2 border-slate-400 bg-white px-6 text-lg font-semibold hover:bg-slate-50 focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          Ir a Tarifas &rsaquo;
        </Link>
      </div>
      <Field
        label={`Un paquete de ${current.package_hours} horas`}
        hint={
          perHour > 0
            ? `${formatUYU(perHour)} la hora, contra ${formatUYU(hour)} pagando por sesión.`
            : undefined
        }
      >
        <input
          type="number"
          min={0}
          value={pack}
          onChange={(event) => setPack(event.target.value)}
          className={INPUT}
        />
      </Field>
      <Field label="Inscripción a un torneo" hint="Es el valor por defecto de un torneo nuevo.">
        <input
          type="number"
          min={0}
          value={entry}
          onChange={(event) => setEntry(event.target.value)}
          className={INPUT}
        />
      </Field>
    </CardForm>
  )
}

function Rules({ current }: { current: SettingsRead }) {
  const save = useSave()
  const [horizon, setHorizon] = useState(String(current.booking_horizon_days))
  const [lead, setLead] = useState(String(current.min_lead_minutes))
  const [free, setFree] = useState(String(current.free_cancellation_minutes))
  const [packHours, setPackHours] = useState(String(current.package_hours))
  const [roundMinutes, setRoundMinutes] = useState(String(current.tournament_minutes))

  const dirty =
    Number(horizon) !== current.booking_horizon_days ||
    Number(lead) !== current.min_lead_minutes ||
    Number(free) !== current.free_cancellation_minutes ||
    Number(packHours) !== current.package_hours ||
    Number(roundMinutes) !== current.tournament_minutes

  return (
    <CardForm
      title="Reglas de reserva"
      dirty={dirty}
      save={save}
      onSubmit={() =>
        save.mutate({
          data: {
            booking_horizon_days: Number(horizon),
            min_lead_minutes: Number(lead),
            free_cancellation_minutes: Number(free),
            package_hours: Number(packHours),
            tournament_minutes: Number(roundMinutes),
          },
        })
      }
    >
      <Field label="Con cuánta anticipación se puede reservar" hint="En días.">
        <input
          type="number"
          min={1}
          value={horizon}
          onChange={(event) => setHorizon(event.target.value)}
          className={INPUT}
        />
      </Field>
      <Field
        label="Hasta cuánto antes del inicio se puede reservar"
        hint="En minutos. Alguien parado en la puerta a las 09:05 todavía puede tomar las 09:10."
      >
        <input
          type="number"
          min={0}
          value={lead}
          onChange={(event) => setLead(event.target.value)}
          className={INPUT}
        />
      </Field>
      <Field
        label="Ventana de cancelación sin costo"
        hint="En minutos antes del inicio. Más tarde que eso, la hora queda acreditada en lugar de devolverse."
      >
        <input
          type="number"
          min={0}
          value={free}
          onChange={(event) => setFree(event.target.value)}
          className={INPUT}
        />
      </Field>
      <Field label="Horas por paquete">
        <input
          type="number"
          min={1}
          value={packHours}
          onChange={(event) => setPackHours(event.target.value)}
          className={INPUT}
        />
      </Field>
      <Field
        label="Duración de una ronda de torneo"
        hint="En minutos. 120 alcanzan para 18 hoyos; 60 para nueve. Se puede cambiar torneo por torneo, y cambiar esto acá no altera los torneos ya creados."
      >
        <input
          type="number"
          min={10}
          step={10}
          value={roundMinutes}
          onChange={(event) => setRoundMinutes(event.target.value)}
          className={INPUT}
        />
      </Field>
    </CardForm>
  )
}

function Coaches({ current }: { current: SettingsRead }) {
  const save = useSave()
  const [coaches, setCoaches] = useState<string[]>(current.coaches)
  const [typed, setTyped] = useState('')

  const dirty = coaches.join(',') !== current.coaches.join(',')

  const add = () => {
    const name = typed.trim()
    if (name && !coaches.includes(name)) setCoaches([...coaches, name])
    setTyped('')
  }

  return (
    <CardForm
      title="Profesores"
      hint="Quiénes pueden dar clases. Aparecen al reservar una."
      dirty={dirty}
      save={save}
      onSubmit={() => save.mutate({ data: { coaches } })}
    >
      <ul className="flex flex-wrap gap-3">
        {coaches.map((name) => (
          <li key={name}>
            <span className="inline-flex min-h-14 items-center gap-3 rounded-full border-2 border-slate-400 pl-5 pr-2 text-xl">
              {name}
              <button
                type="button"
                onClick={() => setCoaches(coaches.filter((other) => other !== name))}
                aria-label={`Quitar a ${name}`}
                className="flex size-11 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100"
              >
                ×
              </button>
            </span>
          </li>
        ))}
        {coaches.length === 0 && (
          <li className="text-lg text-red-700">
            Tiene que haber al menos uno, o no se puede reservar una clase.
          </li>
        )}
      </ul>

      <div className="flex flex-wrap items-end gap-3">
        <Field label="Agregar a alguien">
          <input
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            // Enter adds the name rather than submitting the card, which would
            // save without the name that was just typed.
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                add()
              }
            }}
            placeholder="Nombre"
            className={INPUT}
          />
        </Field>
        <button
          type="button"
          onClick={add}
          className="min-h-14 rounded-md border-2 border-slate-400 px-6 text-lg"
        >
          Agregar
        </button>
      </div>
    </CardForm>
  )
}

function Reminders({ current }: { current: SettingsRead }) {
  const save = useSave()
  const [hours, setHours] = useState(String(current.reminder_hours_before))
  const dirty = Number(hours) !== current.reminder_hours_before

  return (
    <CardForm
      title="Correo de recordatorio"
      dirty={dirty}
      save={save}
      onSubmit={() => save.mutate({ data: { reminder_hours_before: Number(hours) } })}
    >
      <Field label="Horas antes de la sesión" hint="En 0 no se envía ningún recordatorio.">
        <input
          type="number"
          min={0}
          max={48}
          value={hours}
          onChange={(event) => setHours(event.target.value)}
          className={INPUT}
        />
      </Field>
      {Number(hours) === 0 && (
        <p className="text-lg text-amber-800">No se va a enviar ningún recordatorio.</p>
      )}
    </CardForm>
  )
}

function Advanced({ current }: { current: SettingsRead }) {
  const save = useSave()
  const [open, setOpen] = useState(false)
  const [slot, setSlot] = useState(String(current.slot_minutes))
  const [step, setStep] = useState(String(current.step_minutes))
  const [buffer, setBuffer] = useState(String(current.buffer_minutes))
  const [hold, setHold] = useState(String(current.hold_minutes))
  const [orphan, setOrphan] = useState(String(current.orphan_rebook_days))

  const dirty =
    Number(slot) !== current.slot_minutes ||
    Number(step) !== current.step_minutes ||
    Number(buffer) !== current.buffer_minutes ||
    Number(hold) !== current.hold_minutes ||
    Number(orphan) !== current.orphan_rebook_days

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="min-h-12 text-lg text-slate-600 underline"
      >
        Mostrar los ajustes avanzados
      </button>
    )
  }

  return (
    <CardForm
      title="Ajustes avanzados"
      hint="Cambian la forma del calendario. Conviene leer la advertencia."
      dirty={dirty}
      save={save}
      onSubmit={() =>
        save.mutate({
          data: {
            slot_minutes: Number(slot),
            step_minutes: Number(step),
            buffer_minutes: Number(buffer),
            hold_minutes: Number(hold),
            orphan_rebook_days: Number(orphan),
          },
        })
      }
    >
      {/* Not hidden, but not casual either. The coupling below is real and
          invisible: nothing would break loudly, the numbers would just quietly
          stop meaning what they say. */}
      <p className="rounded-md border-2 border-amber-400 bg-amber-50 p-4 text-amber-900">
        <strong>Las horas se cuentan de a una, y una reserva gasta una hora.</strong> Si una sesión
        deja de durar 60 minutos, eso deja de ser cierto: alguien podría gastar una hora de saldo en
        una sesión de 90. Conviene cambiar la duración solo habiéndolo pensado.
      </p>

      <Field label="Cuánto dura una sesión" hint="En minutos.">
        <input
          type="number"
          min={1}
          value={slot}
          onChange={(event) => setSlot(event.target.value)}
          className={INPUT}
        />
      </Field>
      <Field
        label="Cada cuánto empiezan los horarios"
        hint="En minutos. Con 10 quedan 09:00, 09:10, 09:20 y así."
      >
        <input
          type="number"
          min={1}
          value={step}
          onChange={(event) => setStep(event.target.value)}
          className={INPUT}
        />
      </Field>
      <Field
        label="Margen entre dos reservas del mismo simulador"
        hint="En minutos. Con 0, alguien puede pasarse si no hay nadie esperando."
      >
        <input
          type="number"
          min={0}
          value={buffer}
          onChange={(event) => setBuffer(event.target.value)}
          className={INPUT}
        />
      </Field>
      <Field
        label="Cuánto retiene el simulador una reserva impaga"
        hint="En minutos. Solo importa con los pagos activos."
      >
        <input
          type="number"
          min={1}
          value={hold}
          onChange={(event) => setHold(event.target.value)}
          className={INPUT}
        />
      </Field>
      <Field
        label="Días para volver a reservar tras pagar un horario perdido"
        hint="Solo importa con los pagos activos."
      >
        <input
          type="number"
          min={1}
          value={orphan}
          onChange={(event) => setOrphan(event.target.value)}
          className={INPUT}
        />
      </Field>
    </CardForm>
  )
}
